/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type Ref, type RefAttributes } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token, useThemeObserver } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { RovoColorLogo } from './components/RovoColorLogo';
import { RovoLogo } from './components/RovoLogo';
import messages from './messages';
import type { AIButtonProps } from './types';

const atlaskitCssStyles = cssMap({
	pressableStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '48px',
		width: '48px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xxlarge'),
	},
	boxStyles: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error -- raw shadow value intentionally used; no ADS shadow token matches this design
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',

		'&:hover': {
			transform: 'scale(1.125)',
			transition: 'transform 0.1s ease-in-out',
			cursor: 'pointer',
		},
		'&:active': {
			transform: 'scale(1.0625)',
		},
	},
	logoWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'transform 0.2s ease-in-out',
	},
	unreadDot: {
		position: 'absolute',
		bottom: token('space.075'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: '4px',
		height: '4px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		pointerEvents: 'none',
	},
});

export const AIButton: ForwardRefExoticComponent<Omit<AIButtonProps, "ref"> & RefAttributes<HTMLButtonElement>> = forwardRef<HTMLButtonElement, AIButtonProps>(
	({ onClick, isDisabled, onMouseEnter, onMouseLeave, hasUnread }, ref: Ref<HTMLButtonElement>) => {
		const { formatMessage } = useIntl();
		const { colorMode } = useThemeObserver();

		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const isRovoButtonVisibilityExperimentEnabled = FeatureGates.getExperimentValue(
			'rovo_button_visibility_improvements',
			'isEnabled',
			false,
		);

		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const isRovoButtonVisibilityExtendedEnabled = FeatureGates.checkGate(
			'rovo_button_visibility_improvements_extended',
		);

		return (
			<Box xcss={atlaskitCssStyles.boxStyles}>
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStyles}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onFocus={onMouseEnter}
					onBlur={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					<VisuallyHidden>{formatMessage(messages.rovoButton)}</VisuallyHidden>
					{/* Dynamic height — xcss+style used since cssMap cannot express runtime-computed values */}
					<Box
						xcss={atlaskitCssStyles.logoWrapper}
						style={{ transform: hasUnread ? 'scale(0.833) translateY(-3px)' : 'scale(1) translateY(0)' }}
					>
						{isRovoButtonVisibilityExperimentEnabled || isRovoButtonVisibilityExtendedEnabled ? (
							<RovoColorLogo colorMode={colorMode === 'dark' ? 'dark' : 'light'} />
						) : (
							<RovoLogo />
						)}
					</Box>
				</Pressable>
				{hasUnread && (
					<Box
						xcss={atlaskitCssStyles.unreadDot}
						testId="ai-button-unread-dot"
						aria-hidden="true"
					/>
				)}
			</Box>
		);
	},
);
